export const PART_TYPE_META = {
  "track-chain": {
    name: "Track Chains",
    singular: "track chain",
    aka: "track link assemblies · sealed & lubricated chains · track links",
    heroDesc:
      "Buy excavator and bulldozer track chains at manufacturer prices — sealed and lubricated track link assemblies for 350+ machinery brands, built to OEM specification and shipped worldwide.",
    intro: [
      "VANROCK track chains are built from forged links with induction-hardened pins and bushings — supplied as greased & sealed chains for excavators and as oil-lubricated chains with a split master link for bulldozers.",
      "The range covers a wide spread of pitches, from mini excavators up to mining-class machines, across 350+ machinery brands. Select your brand below — each model page lists the matching chains with link counts and dimensions.",
    ],
    faqs: [
      {
        q: "How much does an excavator track chain cost?",
        a: "Track chain prices depend on your machine model, link count and shoe configuration. Request a quote on any track chain page and our sales team responds with direct manufacturer pricing and availability — typically well below OEM dealer list prices.",
      },
      {
        q: "Are VANROCK track chains sealed and lubricated?",
        a: "Yes. Our track chains use sealed and lubricated (SALT) construction with induction-hardened pins and bushings, so internal wear surfaces are protected for the full service life of the chain.",
      },
      {
        q: "Are these OEM or aftermarket track chains?",
        a: "VANROCK track chains are premium aftermarket parts manufactured 100% in our own factory to OEM specifications — same materials, same hardening depth, same fit — at direct-from-manufacturer prices.",
      },
      {
        q: "Do you ship track chains worldwide?",
        a: "Yes. Track chains are dispatched from stock with worldwide delivery. A shipping quote is included with every offer, and full container or mixed consolidations are available for dealers.",
      },
    ],
  },
  "track-group": {
    name: "Track Groups",
    singular: "track group",
    aka: "track group assemblies · chain + shoes assembled · complete tracks",
    heroDesc:
      "Complete track group assemblies (track chain + track shoes, factory-assembled) for excavators and dozers — manufacturer prices, OEM specification, worldwide shipping from stock.",
    intro: [
      "A track group is the complete, ready-to-install track: chain and shoes assembled and torqued to specification in the factory, delivered as a single unit.",
      "Track groups are configured by chain type, link count, shoe width and grouser profile. Select your brand below to see the track groups listed per model.",
    ],
    faqs: [
      {
        q: "What is a track group?",
        a: "A track group is a complete track assembly — track chain plus track shoes bolted and torqued at the factory — ready to install on your machine with no additional assembly work.",
      },
      {
        q: "How much does a complete track group cost?",
        a: "Pricing depends on your machine model, link count, shoe width and grouser type. Submit a quote request with your model (and serial number if possible) and you'll receive direct manufacturer pricing.",
      },
      {
        q: "Track group vs. buying chain and shoes separately — which is better?",
        a: "A factory-assembled track group guarantees matched components, correct bolt torque and saves significant installation time. Buying separately can make sense if only one component is worn — our specialists can advise based on your undercarriage inspection.",
      },
      {
        q: "Which shoe widths are available for track groups?",
        a: "We assemble track groups with the standard and optional shoe widths listed by the OEM for your model. Tell us your application (dirt, rock, finishing) and we'll recommend the right width and grouser count.",
      },
    ],
  },
  "track-roller": {
    name: "Track Rollers",
    singular: "track roller",
    aka: "bottom rollers · lower rollers · single & double flange rollers",
    heroDesc:
      "Excavator and dozer track rollers (bottom rollers) for sale — single flange and double flange, lifetime-lubricated, OEM specification, manufacturer prices for 350+ brands.",
    intro: [
      "VANROCK track rollers (bottom rollers) are forged from alloy steel, deep induction-hardened on the tread, fitted with bimetal bushings and face seals, and lubricated for life.",
      "The range includes single and double flange rollers for excavators, mini excavators and bulldozers across 350+ brands. Select your brand below — each model page lists the exact rollers with dimensions and weights.",
    ],
    faqs: [
      {
        q: "How much does an excavator track roller cost?",
        a: "Bottom roller prices vary by machine size and flange type. Request a quote on any track roller page — our team replies with direct manufacturer pricing, and volume pricing when you replace the full set.",
      },
      {
        q: "Single flange vs double flange track rollers — what's the difference?",
        a: "Single flange rollers guide the chain from one side and are used across most positions; double flange rollers provide extra lateral guidance and are fitted at specific positions per the OEM layout. Model pages list the exact rollers your machine uses.",
      },
      {
        q: "Are VANROCK track rollers lubricated for life?",
        a: "Yes. Every track roller is sealed with metal face seals and filled with lifetime lubrication — no maintenance required for the service life of the roller.",
      },
      {
        q: "Should I replace all bottom rollers at once?",
        a: "If several rollers measure near the wear limit, replacing the full set avoids repeated downtime and keeps the chain running level. We offer set pricing — mention it in your quote request.",
      },
    ],
  },
  "carrier-roller": {
    name: "Carrier Rollers",
    singular: "carrier roller",
    aka: "top rollers · upper rollers · support rollers",
    heroDesc:
      "Carrier rollers (top rollers) for excavators and dozers — lifetime-lubricated upper rollers at manufacturer prices, OEM specification, for 350+ machinery brands.",
    intro: [
      "VANROCK carrier rollers (top rollers) support the upper run of the track chain, with hardened tread and flange, sealed and lubricated for life.",
      "Available for excavators, bulldozers and crawler loaders across 350+ brands. Select your brand below to see the carrier rollers listed per model.",
    ],
    faqs: [
      {
        q: "What does a carrier roller do?",
        a: "Carrier rollers support the returning (top) run of the track chain, preventing sag and keeping the chain aligned with the idler and sprocket. Most excavators use one or two per side.",
      },
      {
        q: "How do I know my carrier roller needs replacing?",
        a: "Common signs are visible tread wear or flat spots, oil leakage from the seal, a roller that no longer spins freely, or excessive chain sag that persists after tensioning.",
      },
      {
        q: "How much does a carrier roller cost?",
        a: "Prices depend on machine model and roller size. Request a quote on the part page for your model — direct manufacturer pricing.",
      },
      {
        q: "Are these carrier rollers OEM quality?",
        a: "Yes — VANROCK carrier rollers are manufactured to OEM specifications from forged alloy steel with deep-hardened treads and sealed lifetime lubrication, made 100% in our own factory.",
      },
    ],
  },
  "track-shoe": {
    name: "Track Shoes",
    singular: "track shoe",
    aka: "grouser shoes · track pads · track plates",
    heroDesc:
      "Track shoes (grouser shoes, track pads) for excavators and dozers — single, double and triple grouser, all widths, manufacturer prices for 350+ brands with worldwide shipping.",
    intro: [
      "VANROCK track shoes are rolled from boron and alloy steel profiles and heat-treated for a balance of wear resistance and toughness. Single, double and triple grouser profiles are available, as well as special shapes.",
      "Widths follow the OEM options for each model — from narrow rock shoes to wide flotation shoes. Select your brand below; each model page lists the shoe widths and configurations.",
    ],
    faqs: [
      {
        q: "How much do excavator track shoes cost?",
        a: "Track shoe prices depend on width, grouser style and quantity. Request a quote with your machine model and shoe width — full-set pricing with bolts and nuts included is available and usually the most economical.",
      },
      {
        q: "Which track shoe width should I choose?",
        a: "Wider shoes lower ground pressure for soft terrain; narrower shoes resist bending on rock and hard ground. Stick to OEM-approved widths for your model — our specialists can confirm the right width for your application.",
      },
      {
        q: "Single, double or triple grouser — what's the difference?",
        a: "Triple grouser shoes (standard on excavators) balance traction and smooth turning. Single grouser shoes (dozers) maximize traction. Double grouser is a middle ground often used on loaders and older dozers.",
      },
      {
        q: "Do you supply track shoes with bolts and nuts?",
        a: "Yes — we recommend fitting new track bolts and nuts with every shoe set. Ask for hardware in your quote and we'll include the exact OEM-spec bolt kit.",
      },
    ],
  },
  idler: {
    name: "Idlers",
    singular: "idler",
    aka: "front idlers · idler wheels · idler assemblies",
    heroDesc:
      "Front idlers and idler assemblies for excavators and dozers — complete with brackets and tension springs on request, OEM specification, manufacturer prices for 350+ brands.",
    intro: [
      "VANROCK idlers are made from tough steel alloys with induction-hardened running surfaces, assembled with face seals, oil-filled and leak-tested. Supplied as bare idlers or complete idler assemblies with brackets.",
      "Available for excavators, mini excavators and bulldozers across 350+ brands. Select your brand below — each model page lists the idlers with dimensions.",
    ],
    faqs: [
      {
        q: "What are the signs of a worn front idler?",
        a: "Look for tread wear beyond the OEM limit, oil weeping from the center seal, visible flange wear, or a track that keeps de-tensioning or tracking off-centre.",
      },
      {
        q: "How much does an excavator idler cost?",
        a: "Idler prices vary with machine size and whether you need the bare idler or the complete assembly with bracket and tension unit. Request a quote for your model — direct manufacturer pricing.",
      },
      {
        q: "Do you sell complete idler assemblies?",
        a: "Yes — bare idlers, idlers with brackets, and complete idler groups with recoil springs where applicable. Tell us your model and preferred configuration in the quote request.",
      },
      {
        q: "Are VANROCK idlers OEM quality?",
        a: "Yes. Idlers are made in our own factory to OEM specifications, with induction-hardened wear surfaces, metal face seals and lifetime lubrication, and 100% final inspection.",
      },
    ],
  },
  sprocket: {
    name: "Sprockets & Segments",
    singular: "sprocket",
    aka: "drive sprockets · sprocket segments · segment groups · rims",
    heroDesc:
      "Drive sprockets, sprocket segments and segment groups for excavators and dozers — precision-cut teeth, deep-hardened, manufacturer prices for 350+ machinery brands.",
    intro: [
      "VANROCK supplies one-piece drive sprockets for excavators and bolt-on segment groups for bulldozers, with induction-hardened teeth cut to the OEM profile.",
      "Tooth counts and bolt patterns follow the machine specification. Select your brand below to see the sprockets and segments listed per model.",
    ],
    faqs: [
      {
        q: "When should I replace the sprocket?",
        a: "Replace the sprocket when teeth show visible sharpening, hooking or tip wear — and always consider replacing it together with a new chain, since a worn sprocket destroys new bushings within hundreds of hours.",
      },
      {
        q: "Sprocket vs segment group — which does my machine use?",
        a: "Most excavators use a one-piece bolt-on sprocket; most dozers use segmented rims (segment groups) that can be replaced without breaking the chain. Your model page lists the exact configuration.",
      },
      {
        q: "How much does a sprocket or segment set cost?",
        a: "Prices depend on machine size and tooth count. Request a quote on the part page for your model and receive direct manufacturer pricing.",
      },
      {
        q: "Are the teeth hardened?",
        a: "Yes — every VANROCK sprocket and segment has deep induction-hardened teeth with controlled hardness gradients, cut to OEM profile for exact chain engagement.",
      },
    ],
  },
  "rubber-track": {
    name: "Rubber Tracks",
    singular: "rubber track",
    aka: "rubber tracks for mini excavators · CTL tracks · rubber crawler tracks",
    heroDesc:
      "Rubber tracks for mini excavators and compact track loaders — steel-cord reinforced, all sizes, manufacturer prices for 350+ brands with worldwide shipping from stock.",
    intro: [
      "VANROCK rubber tracks are built with continuous steel cords and forged metal cores bonded into an abrasion-resistant rubber compound, in both long-pitch and short-pitch designs.",
      "Sizes cover mini and midi excavators, compact track loaders and tracked dumpers across 350+ brands. Sizes read width × pitch × link count (e.g. 300x52.5x84) — select your brand below or check your model page.",
    ],
    faqs: [
      {
        q: "How do I find my rubber track size?",
        a: "The size is moulded on the inside of your current track as width × pitch × link count (e.g. 300x52.5x84). If it's unreadable, send us your machine model and serial number and we'll confirm the correct size.",
      },
      {
        q: "How much does a rubber track for a mini excavator cost?",
        a: "Prices depend on size and quantity — pairs are cheaper per track than singles. Request a quote with your machine model or track size and receive direct manufacturer pricing.",
      },
      {
        q: "Are VANROCK rubber tracks steel-reinforced?",
        a: "Yes — every track has continuous (jointless) steel cords and forged, hardened metal cores bonded into a cut- and abrasion-resistant rubber compound.",
      },
      {
        q: "How long do rubber tracks last?",
        a: "Typical service life is 1,200–2,000 hours depending on surface and operating style. Avoiding sharp pivot turns on concrete and keeping correct tension significantly extends track life.",
      },
    ],
  },
  "track-bolt-nut": {
    name: "Track Bolts & Nuts",
    singular: "track bolt",
    aka: "track shoe bolts · master bolts · track hardware kits",
    heroDesc:
      "Track bolts, track nuts and master bolts for excavators and dozers — grade 12.9 hardware to OEM torque spec, sold per piece or in full-set kits at manufacturer prices.",
    intro: [
      "VANROCK track bolts and nuts are forged from alloy steel and heat-treated to strength class 12.9 (track bolts) and 10.9 (sprocket and roller bolts), with rolled threads to OEM specification.",
      "Hardware is listed per chain and shoe combination — select your brand below to find the bolts and nuts matched to your model, individually or as full-set kits.",
    ],
    faqs: [
      {
        q: "Should track bolts be replaced with new shoes?",
        a: "Yes — always. Track bolts stretch when torqued; reused bolts lose clamping force and work loose, damaging both shoes and chain links. New OEM-spec bolts are cheap insurance.",
      },
      {
        q: "What grade are VANROCK track bolts?",
        a: "Track bolts are cold-forged grade 12.9 alloy steel with rolled threads and matching hardened track nuts, meeting OEM torque and preload specifications.",
      },
      {
        q: "Can I buy a complete hardware kit for my shoe set?",
        a: "Yes — tell us your machine model and shoe count, and we'll quote the exact number of bolts and nuts (including master bolts) needed for a full re-shoe.",
      },
      {
        q: "How much do track bolts and nuts cost?",
        a: "Hardware is priced per piece with volume discounts on full-set kits. Request a quote with your model — response.",
      },
    ],
  },
  "rubber-pad": {
    name: "Rubber Pads",
    singular: "rubber pad",
    aka: "bolt-on pads · chain-on pads · clip-on pads · road pads",
    heroDesc:
      "Rubber pads for steel-tracked excavators — bolt-on, chain-on, HD VGRIP, clip-on and road pad versions, each pad individually vulcanised onto a metal core. Sizes for link pitches 90–216 mm.",
    intro: [
      "Rubber pads let a steel-tracked machine work on asphalt, concrete and finished surfaces without damage. Each VANROCK rubber pad is individually vulcanised onto a metal core and is matched to the chain by its link pitch.",
      "Four mounting systems cover every configuration: bolt-on pads screwed through the track shoe, chain-on pads mounted directly on the chain (standard and HD VGRIP), clip-on pads clamped around the fitted shoe, and road pads in rubber or polyurethane. Bolt-on and chain-on pads are supplied with bolts and nuts.",
    ],
    faqs: [
      {
        q: "Which rubber pad type fits my machine?",
        a: "The link pitch of your track chain determines the size; the mounting system determines the type. Chain-on pads mount directly on the chain, bolt-on pads screw through drilled track shoes, and clip-on pads clamp around the existing shoe. If you are unsure, send your machine model and a photo of the track — our team confirms the correct pad.",
      },
      {
        q: "How do I measure for clip-on rubber pads?",
        a: "Clip-on pads clamp around the track shoe, so the shoe profile must match. Shoe profiles differ slightly between manufacturers — measure the shoe width and grouser spacing before ordering. Clip-on pads always fit in combination with VANROCK track shoes.",
      },
      {
        q: "Are bolts and nuts included with rubber pads?",
        a: "Yes — bolt-on and chain-on rubber pads (including HD VGRIP) are supplied complete with bolts and nuts. Clip-on pads clamp mechanically and need no separate hardware.",
      },
      {
        q: "What is the difference between rubber and polyurethane road pads?",
        a: "Rubber road pads offer more grip. Polyurethane road pads are cast directly onto the track shoe — extremely wear-resistant, suitable for heavy machinery and resistant to oils, petrol and grease.",
      },
    ],
  },
};

export const PART_TYPE_ORDER = [
  "track-chain",
  "track-group",
  "track-roller",
  "carrier-roller",
  "track-shoe",
  "idler",
  "sprocket",
  "rubber-track",
  "rubber-pad",
  "track-bolt-nut",
];
